import {
  DeleteOutlined,
  InfoCircleOutlined,
  PlusOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons'
import {Button, Divider, Input, InputRef, Popconfirm, Select, Space, Spin, Tooltip} from 'antd'
import React, {useRef, useState} from 'react'
import {useIntl} from 'react-intl'
import {GardenPlanVariant} from '../../entities/gardenPlanVariant'
import {instance as gardenPlanVariantsService} from '../../services/GardenPlanVariantsService'
import './GardenPlanVariantSelector.less'
import {GardenPlanVariantEditButton} from './GardenPlanVariantEditButton'

export type GardenPlanVariantSelectorProps = {
  projectId: string
  gardenPlanVariants: GardenPlanVariant[]
  activeGardenPlanVariantId: string | null
  onActiveGardenPlanVariantIdChange: (id: string | null) => void
  onGardenPlanVariantsListUpdated: () => void
  className?: string
}

const DEFAULT_VARIANT_VALUE = ''
const MIN_VISIBLE_NAME_CHARS = 10

function getNameSizerText(name: string) {
  return name.length > MIN_VISIBLE_NAME_CHARS ? `${name.slice(0, MIN_VISIBLE_NAME_CHARS)}…` : name
}

export function GardenPlanVariantSelector({
  projectId,
  gardenPlanVariants = [],
  activeGardenPlanVariantId,
  onActiveGardenPlanVariantIdChange = () => {},
  onGardenPlanVariantsListUpdated = () => {},
  className,
}: GardenPlanVariantSelectorProps) {
  const intl = useIntl()

  const inputRef = useRef<InputRef>(null)
  const [name, setName] = useState('')
  const [loading, setLoading] = useState(false)

  async function createGardenPlanVariant(
    e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>
  ) {
    e.preventDefault()
    if (loading || !name.trim()) {
      return
    }
    setLoading(true)
    try {
      const result = await gardenPlanVariantsService.createGardenPlanVariant({
        projectId,
        name: name.trim(),
      })
      if (result.success) {
        onGardenPlanVariantsListUpdated()
      }
    } finally {
      setName('')
      setLoading(false)
    }
  }

  async function applyGardenPlanVariant(variantId: string) {
    setLoading(true)
    try {
      const result = await gardenPlanVariantsService.applyGardenPlanVariantToProject(variantId)
      if (result.success) {
        onActiveGardenPlanVariantIdChange(null)
        onGardenPlanVariantsListUpdated()
      }
    } finally {
      setLoading(false)
    }
  }

  async function deleteGardenPlanVariant(variantId: string) {
    setLoading(true)
    try {
      const result = await gardenPlanVariantsService.deleteGardenPlanVariant(variantId)
      if (result.success) {
        onGardenPlanVariantsListUpdated()
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <Select
      className={className ? `garden-plan-variants ${className}` : 'garden-plan-variants'}
      placeholder={intl.formatMessage({id: 'gardenPlan'})}
      prefix={
        <Tooltip title={intl.formatMessage({id: 'gardenPlanVariantsHint'})}>
          <InfoCircleOutlined className="garden-plan-variants__hint" />
        </Tooltip>
      }
      value={activeGardenPlanVariantId ?? DEFAULT_VARIANT_VALUE}
      onChange={(value: string) => onActiveGardenPlanVariantIdChange(value || null)}
      labelRender={({label, value}) => (
        <div className="garden-plan-variant-item">
          <span className="garden-plan-variant-item__name garden-plan-variant-item__name--selected">
            {/* invisible, reserves the width of the name's first characters */}
            <span className="garden-plan-variant-item__sizer" aria-hidden>
              {getNameSizerText(String(label))}
            </span>
            <span>{label}</span>
          </span>
          {value !== DEFAULT_VARIANT_VALUE && (
            // keep the click from opening the dropdown
            <span
              className="apply-garden-plan-variant"
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
            >
              <Popconfirm
                placement="bottom"
                title={intl.formatMessage({id: 'applyGardenPlanTitle'}, {planName: label})}
                description={intl.formatMessage({id: 'applyGardenPlanDescription'})}
                icon={<QuestionCircleOutlined />}
                onConfirm={() => applyGardenPlanVariant(value as string)}
                okText={intl.formatMessage({id: 'yes'})}
                cancelText={intl.formatMessage({id: 'no'})}
              >
                <Button color="primary" variant="outlined" size="small" loading={loading}>
                  {intl.formatMessage({id: 'apply'})}
                </Button>
              </Popconfirm>
            </span>
          )}
        </div>
      )}
      options={[
        {
          value: DEFAULT_VARIANT_VALUE,
          label: intl.formatMessage({id: 'defaultProjectWithoutPlans'}),
          variant: null,
        },
        ...gardenPlanVariants.map((variant) => ({value: variant.id, label: variant.name, variant})),
      ]}
      classNames={{
        popup: {
          root: 'garden-plan-variants-dropdown',
        },
      }}
      popupRender={(menu) => (
        <Spin spinning={loading} delay={300}>
          {menu}
          <Divider style={{margin: '8px 0'}} />
          <Space style={{padding: '0 8px 4px'}}>
            <Input
              placeholder={intl.formatMessage({id: 'newGardenPlan'})}
              ref={inputRef}
              value={name}
              onChange={(event) => setName(event.target.value)}
              onKeyDown={(e) => e.stopPropagation()}
            />
            <Button type="text" icon={<PlusOutlined />} onClick={createGardenPlanVariant}>
              {intl.formatMessage({id: 'add'})}
            </Button>
          </Space>
        </Spin>
      )}
      optionRender={(option) => (
        <div className="garden-plan-variant-item">
          <span className="garden-plan-variant-item__name">{option.data.label}</span>
          {option.data.variant && (
            <GardenPlanVariantEditButton
              gardenPlanVariant={option.data.variant}
              onGardenPlanVariantEdited={onGardenPlanVariantsListUpdated}
            />
          )}
          {option.data.variant && (
            <span onClick={(e) => e.stopPropagation()}>
              <Popconfirm
                placement="bottom"
                title={intl.formatMessage({id: 'areYouSureYouWantToDeleteThisPlan'})}
                description={intl.formatMessage({id: 'thisOperationIsIrreversible'})}
                icon={<QuestionCircleOutlined style={{color: 'red'}} />}
                onConfirm={() => deleteGardenPlanVariant(option.value as string)}
                okText={intl.formatMessage({id: 'yes'})}
                cancelText={intl.formatMessage({id: 'no'})}
              >
                <DeleteOutlined className="delete-garden-plan-variant" />
              </Popconfirm>
            </span>
          )}
        </div>
      )}
    />
  )
}
